import { api } from './api'

function assertSuccess(body: { success?: boolean; message?: string; data?: unknown }) {
  if (!body?.success) {
    const err = new Error(body?.message || 'Request failed') as Error & { code?: string }
    throw err
  }
  return body.data
}

function apiError(e: unknown) {
  const err = e as {
    response?: { data?: { message?: string; error?: { code?: string } }; status?: number }
    message?: string
  }
  const next = new Error(err.response?.data?.message || err.message || 'Request failed') as Error & {
    code?: string
    status?: number
  }
  next.code = err.response?.data?.error?.code
  next.status = err.response?.status
  return next
}

export type PlatformPricingLocale = 'en' | 'de' | 'es' | 'fr'

export type PlatformPlanLocaleContent = {
  name: string
  tagline: string
  features: string[]
  cta: string
  pricePrefix?: string
}

export type PlatformPricingSectionLocaleContent = {
  kicker: string
  title: string
  subtitle: string
  note: string
  monthSuffix: string
  yearSuffix: string
  recommendedLabel: string
}

export type PlatformPlanCurrencyPrices = {
  priceAmount: number | null
  yearlyPriceAmount: number | null
}

export type PlatformPricingPlan = {
  code: 'standard' | 'premium' | 'plus' | 'enterprise'
  sortOrder: number
  active: boolean
  recommended: boolean
  priceType: 'monthly' | 'from' | 'contact'
  /** Legacy / default-currency amount (kept in sync with pricesByCurrency[doc.currency]). */
  priceAmount: number | null
  yearlyPriceAmount: number | null
  pricesByCurrency?: Partial<Record<'EUR' | 'NGN' | string, PlatformPlanCurrencyPrices>>
  maxStaffCount: number | null
  locales: Partial<Record<PlatformPricingLocale, PlatformPlanLocaleContent>>
}

export type PlatformSubscriptionSettings = {
  trial: {
    enabled: boolean
    durationDays: number
    eligiblePlanCodes: PlatformPricingPlan['code'][]
  }
  requireCardUpfront: boolean
  expiry: {
    hardBlockStorefront: boolean
    gracePeriodDays: number
  }
}

export type PlatformPricingDocument = {
  defaultLocale: PlatformPricingLocale
  currency: string
  sectionLocales: Partial<Record<PlatformPricingLocale, PlatformPricingSectionLocaleContent>>
  plans: PlatformPricingPlan[]
  subscriptionSettings?: PlatformSubscriptionSettings
  availableLocales?: string[]
}

export const PLATFORM_PRICING_LOCALES: PlatformPricingLocale[] = ['en', 'de', 'es', 'fr']

export async function fetchAdminPlatformPricing(): Promise<PlatformPricingDocument> {
  try {
    const { data } = await api.get('/admin/platform/pricing')
    return assertSuccess(data) as PlatformPricingDocument
  } catch (e) {
    throw apiError(e)
  }
}

export async function patchAdminPlatformPricing(
  body: Partial<PlatformPricingDocument>,
): Promise<PlatformPricingDocument> {
  try {
    const { data } = await api.patch('/admin/platform/pricing', body)
    return assertSuccess(data) as PlatformPricingDocument
  } catch (e) {
    throw apiError(e)
  }
}
